/**
 * ReportServer — unified HTTP server for serving streaming reports and artifacts.
 *
 * Binds to 127.0.0.1 only. Auto-selects a port starting at 9323.
 * 30-minute inactivity timeout with auto-shutdown.
 */

import { createServer, type Server, type IncomingMessage, type ServerResponse } from 'node:http';
import { join, dirname } from 'node:path';
import { existsSync, readdirSync } from 'node:fs';
import {
  setCorsHeaders,
  sendJson,
  handleHealth,
  handleSummary,
  handleTests,
  handleTestDetail,
  handleTestDataFile,
  handleFiles,
  handleArtifactsList,
  handleArtifactsDeleteAll,
  handleArtifactDelete,
  handleShutdown,
  serveStaticFile,
} from './report-server-routes.js';

const DEFAULT_PORT = 9323;
const MAX_PORT_ATTEMPTS = 10;
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

export interface ReportServerOptions {
  port?: number;
  htmlPath?: string;
}

export interface ReportServerHandle {
  port: number;
  dispose: () => Promise<void>;
}

export function startReportServer(
  reportDir: string,
  options?: ReportServerOptions,
): Promise<ReportServerHandle> {
  return new Promise((resolve, reject) => {
    const startPort = options?.port ?? DEFAULT_PORT;
    const startTime = Date.now();
    let inactivityTimer: ReturnType<typeof setTimeout>;
    let currentAttempt = 0;

    // Determine artifacts directory — check both locations
    const artifactsDir = existsSync(join(reportDir, 'artifacts'))
      ? join(reportDir, 'artifacts')
      : existsSync(join(reportDir, '..', 'artifacts'))
        ? join(reportDir, '..', 'artifacts')
        : join(reportDir, 'artifacts');

    function resetTimer(): void {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        server.close();
      }, INACTIVITY_TIMEOUT_MS);
    }

    // Resolve the HTML report path — explicit option, or find it in the parent dir
    let htmlReportPath = options?.htmlPath ?? null;
    if (!htmlReportPath) {
      const parentDir = dirname(reportDir);
      try {
        const htmlFile = readdirSync(parentDir).find(f => f.endsWith('.html'));
        if (htmlFile) htmlReportPath = join(parentDir, htmlFile);
      } catch { /* ignore */ }
    }

    const server: Server = createServer((req: IncomingMessage, res: ServerResponse) => {
      resetTimer();
      setCorsHeaders(res);

      if (req.method === 'OPTIONS') {
        res.writeHead(204);
        res.end();
        return;
      }

      let pathname: string;
      try {
        const url = new URL(req.url ?? '/', `http://${req.headers.host ?? 'localhost'}`);
        pathname = url.pathname;
      } catch {
        sendJson(res, 400, { error: 'Invalid URL' });
        return;
      }

      // --- Serve HTML report at root ---
      if (req.method === 'GET' && (pathname === '/' || pathname === '/index.html')) {
        if (htmlReportPath && existsSync(htmlReportPath)) {
          serveStaticFile(req, res, htmlReportPath);
          return;
        }
        sendJson(res, 404, { error: 'HTML report not found' });
        return;
      }

      // --- Report API routes ---
      if (req.method === 'GET' && pathname === '/api/health') {
        handleHealth(req, res, reportDir, startTime);
        return;
      }
      if (req.method === 'GET' && pathname === '/api/summary') {
        handleSummary(req, res, reportDir);
        return;
      }
      if (req.method === 'GET' && pathname === '/api/tests') {
        handleTests(req, res, reportDir);
        return;
      }
      if (req.method === 'GET' && pathname === '/api/files') {
        handleFiles(req, res, reportDir);
        return;
      }

      // GET /api/tests/:id/network, /api/tests/:id/console, /api/tests/:id/api-calls
      const testDataMatch = pathname.match(/^\/api\/tests\/([a-f0-9]+)\/(network|console|api-calls)$/);
      if (req.method === 'GET' && testDataMatch) {
        handleTestDataFile(req, res, reportDir, testDataMatch[1], testDataMatch[2] as 'network' | 'console' | 'api-calls');
        return;
      }

      // GET /api/tests/:id
      const testMatch = pathname.match(/^\/api\/tests\/([a-f0-9]+)$/);
      if (req.method === 'GET' && testMatch) {
        handleTestDetail(req, res, reportDir, testMatch[1]);
        return;
      }

      // --- Artifact routes ---
      if (req.method === 'GET' && pathname === '/api/artifacts') {
        handleArtifactsList(req, res, artifactsDir);
        return;
      }
      if (req.method === 'DELETE' && pathname === '/api/artifacts') {
        handleArtifactsDeleteAll(req, res, artifactsDir);
        return;
      }
      const deleteMatch = pathname.match(/^\/api\/artifacts\/(.+)$/);
      if (req.method === 'DELETE' && deleteMatch) {
        handleArtifactDelete(req, res, artifactsDir, decodeURIComponent(deleteMatch[1]));
        return;
      }

      // --- Static artifact files ---
      if (req.method === 'GET' && pathname.startsWith('/artifacts/')) {
        const relPath = decodeURIComponent(pathname.slice('/artifacts/'.length));
        // Path traversal protection
        if (relPath.includes('..') || relPath.includes('\0')) {
          sendJson(res, 400, { error: 'Invalid path' });
          return;
        }
        serveStaticFile(req, res, join(artifactsDir, relPath));
        return;
      }

      // --- Shutdown ---
      if (req.method === 'POST' && pathname === '/api/shutdown') {
        handleShutdown(req, res, server);
        return;
      }

      sendJson(res, 404, { error: 'Not found' });
    });

    function tryListen(port: number): void {
      const errorHandler = (err: NodeJS.ErrnoException): void => {
        if (err.code === 'EADDRINUSE' && currentAttempt < MAX_PORT_ATTEMPTS) {
          currentAttempt++;
          tryListen(port + 1);
        } else {
          reject(err);
        }
      };
      server.once('error', errorHandler);

      server.listen(port, '127.0.0.1', () => {
        // Remove the listen-phase error handler to avoid stale handlers
        server.removeListener('error', errorHandler);
        const addr = server.address();
        if (!addr || typeof addr === 'string') {
          reject(new Error('Failed to get server address'));
          return;
        }
        resetTimer();
        resolve({
          port: addr.port,
          dispose: () => new Promise<void>((res) => {
            clearTimeout(inactivityTimer);
            server.close(() => res());
          }),
        });
      });
    }

    tryListen(startPort);
  });
}
